import { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link, useSearchParams } from 'react-router-dom';

import * as authApi from '../api/auth';

/**
 * Email-verification landing page. The link in the verification
 * email points here with `?token=...`; we POST it to the API and
 * show success/failure.
 *
 * Strict mode in dev fires the effect twice — guard with a ref so
 * the same token isn't POSTed twice (the second call would 400
 * because the row is already consumed, harmless but ugly).
 */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<'pending' | 'ok' | 'fail'>('pending');
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    authApi
      .verifyEmail(token)
      .then(() => setState('ok'))
      .catch((err) => {
        setError(
          (err as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ?? 'verification failed',
        );
        setState('fail');
      });
  }, [token]);

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8 }}>
        <Alert severity="error">No verification token in the URL.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Stack spacing={3} alignItems="center">
        <Typography variant="h4">Verify email</Typography>
        {state === 'pending' && <CircularProgress />}
        {state === 'ok' && (
          <>
            <Alert severity="success">
              Your email is verified. You can sign in now.
            </Alert>
            <MuiLink component={Link} to="/login">
              Sign in
            </MuiLink>
          </>
        )}
        {state === 'fail' && (
          <>
            <Alert severity="error">{error ?? 'Verification failed.'}</Alert>
            <Typography variant="body2" color="text.secondary">
              The link may have expired (24 hours) or already been used.
              Try signing in — if your account is already verified, you'll
              be in.
            </Typography>
            <MuiLink component={Link} to="/login">
              Sign in
            </MuiLink>
          </>
        )}
      </Stack>
    </Container>
  );
}
